import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { AuthContext } from '@ryanar/react-auth-provider';
import { WarningModal } from 'components/modals';
import api from 'api/api';
import auth, { SESSION_START } from 'utils/auth';
import TimerCountDown from 'utils/timer';
import { getMinutes, getSeconds } from 'utils/formatters';
import { NETWORK_REQUIRED } from 'utils/constants';
import sessionStore from 'store/sessionStore';
import { IApiResponse } from 'utils/models';

const SessionTimer = () => {
  const timer = useMemo(() => new TimerCountDown(), []);
  const [sessionStart, setSessionStart] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number | undefined>();
  const [showModal, setShowModal] = useState(false);
  const [submittingRefresh, setSubmitting] = useState(false);

  const oneMinuteLeft = useMemo(() => (timeLeft != null && timeLeft <= 60), [timeLeft]);
  const timeIsOver = useMemo(() => (timeLeft != null && timeLeft <= 0), [timeLeft]);

  const requestRef = useRef<number | undefined>();

  const { setAuthenticated } = React.useContext(AuthContext);
  const { isSessionTimeout } = sessionStore;

  const onLogout = useCallback(() => {
    setShowModal(false);
    setAuthenticated(false);
  }, [setAuthenticated]);

  const startTimer = (session: string | null) => {
    if (session) {
      const now = Number(new Date());
      const start = Number(session);
      const end = Number(new Date(start + Number(auth.data?.sessionTimeoutInSeconds) * 1000));

      const difference = Math.round((end - now) / 1000);

      timer.start(difference);
    } else {
      onLogout();
    }
  };

  const animate: FrameRequestCallback = () => {
    const session = localStorage.getItem(SESSION_START);
    if (sessionStart !== session) {
      setSessionStart(session);
      startTimer(session);
    }
    setTimeLeft(timer.time);
    requestRef.current = requestAnimationFrame(animate);
  };

  const onRefreshSession = () => {
    setSubmitting(true);
    api
      .post('servicereq/refreshSession')
      .then(() => {
        localStorage.setItem(SESSION_START, JSON.stringify(Date.now()));
        timer.start(5 * 60);
      })
      .catch((error: IApiResponse) => {
        console.error(error.description || NETWORK_REQUIRED);
      })
      .finally(() => setSubmitting(false));
  };

  useEffect(() => {
    if (isSessionTimeout) {
      setShowModal(false);
      return;
    }

    if (oneMinuteLeft) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [isSessionTimeout, oneMinuteLeft]);

  useEffect(() => {
    if (timeIsOver) {
      onLogout();
    }
  }, [onLogout, timeIsOver]);

  useEffect(() => {
    const session = localStorage.getItem(SESSION_START);
    setSessionStart(session);
    startTimer(session);
    if (session) {
      requestRef.current = requestAnimationFrame(animate);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <WarningModal
      open={showModal}
      onClose={onRefreshSession}
      cancelName="Keep Me Logged In"
      noIcon
      showCloseIcon={false}
      submitting={submittingRefresh}
    >
      <>
        <span>Your session will expire in {getMinutes(timeLeft)}:{getSeconds(timeLeft)}</span>
        <span>You will automatically be logged out</span>
      </>
    </WarningModal>
  );
};

export default observer(SessionTimer);
